import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  INJECTOR,
  Injector,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltip } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';
import {
  map,
  startWith,
  tap,
} from 'rxjs/operators';
import {
  SelectRowService,
  TableDataSourceDirective,
  AbstractTableRowAction,
  TableRowActionMethod,
  EUROGARD_TABLE_ROW_ACTION_METHOD, TableRowActionStatus,
} from 'angular-components/table-system';

@Directive({
  selector: 'button[eurogardTableRowHeaderDialogAction]',
  standalone: true,
  // eslint-disable-next-line @angular-eslint/no-inputs-metadata-property
  inputs: [ 'errorMessage', 'successMessage', 'refresh', 'color' ],
})
export class TableRowHeaderDialogActionDirective<Data extends Record<string, any>> extends AbstractTableRowAction<Data> implements OnInit, OnDestroy {

  public override readonly isHeader = true;

  private _subscription?: Subscription;

  @Input()
  public override refresh = true;

  constructor(
    @Inject(Renderer2) renderer: Renderer2,
    @Inject(Overlay) overlay: Overlay,
    @Inject(ElementRef) elementRef: ElementRef,
    @Inject(EUROGARD_TABLE_ROW_ACTION_METHOD) actionMethod: TableRowActionMethod<Data>,
    @Inject(ChangeDetectorRef) cdr: ChangeDetectorRef,
    @Inject(ViewContainerRef) vcr: ViewContainerRef,
    @Inject(TableDataSourceDirective) tableDataSourceDirective: TableDataSourceDirective,
    @Inject(MatSnackBar) snackBar: MatSnackBar,
    @Optional() @Inject(MatButton) matButton: MatButton | null,
    @Optional() @Inject(MatTooltip) matTooltip: MatTooltip | null,
    @Inject(INJECTOR) injector: Injector,
    @Optional() @Inject(SelectRowService)
    private readonly selectRowService: SelectRowService<Data> | null,
  ) {
    super(
      renderer,
      overlay,
      elementRef,
      actionMethod,
      cdr,
      vcr,
      tableDataSourceDirective,
      snackBar,
      matButton,
      matTooltip,
      injector,
    );
  }

  public override ngOnDestroy() {
    this._subscription?.unsubscribe();
  }

  public override ngOnInit() {
    if (this.selectRowService) {
      this._subscription = this.selectRowService.selectedRows$
      .pipe(
        startWith(this.selectRowService.selectedRows),
        map((rows) => rows.length !== 0),
        tap((hasSelected) => {
          if (hasSelected) {
            this.setButtonEnabled();
          } else {
            this.setButtonDisabled();
          }
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
    } else {
      this.setButtonDisabled();
    }
  }

  protected override getElementList(): Data[] {
    return this.selectRowService?.selectedRows ?? [];
  }

  public override onClick($event: Event) {
    this.execute().then(() => this.selectRowService?.clear());
    return Promise.resolve();
  }

  public override async execute(): Promise<void> {
    if (this.getButtonDisabled()) {
      return Promise.resolve();
    }
    this.setStatus(TableRowActionStatus.EXECUTING);
    try {
      const typedActionMethods = this.findTypedActionMethod();

      if (!typedActionMethods || typedActionMethods.length !== 1) {
        return Promise.resolve();
      }

      await typedActionMethods[0].call(this.getElementList());

      this.setStatus(TableRowActionStatus.SUCCESS);
    } catch (e: any) {
      console.error(`Failed to execute row action: ${ e.message }`);
      this.setStatus(TableRowActionStatus.ERROR);
    }
  }

  @Input({
    required: true,
    alias: 'eurogardTableRowHeaderDialogAction',
  })
  public override type!: string;

}
